import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { normalizeStudentRecord, buildStudentPersonName } from "./student-record";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Record1098T } from "./interface/1098-T-interface";
import { personNameControl } from "../../constants/recipientIdentity";
import {
  generateRandomRecordId,
  generateUniqueTransmissionId,
  generateUniqueSubmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { parseAmount } from "../../constants/validationFunctions";

/**
 * Helper function to format SoftwareId to ensure first two digits match tax year
 *
 * According to IRS documentation:
 * - New Software IDs are assigned annually for each tax year
 * - Software IDs must start with the last two digits of the tax year (e.g., "25" for 2025)
 * - The Software ID assigned by IRS for 2025 should already have this format
 *
 * @param softwareId - The Software ID from environment variable
 * @param taxYear - The tax year (e.g., "2025")
 * @returns Formatted Software ID with first two digits matching the tax year
 */
function formatSoftwareId(
  softwareId: string | undefined,
  taxYear: string
): string {
  const yearSuffix = taxYear.slice(-2);

  if (!softwareId) {
    console.warn(
      `No Software ID provided. Using default. Please use the IRS-assigned Software ID for tax year ${taxYear}.`
    );
    return `${yearSuffix}00000`;
  }

  if (softwareId.startsWith(yearSuffix)) {
    return softwareId.substring(0, 10);
  }

  console.warn(
    `Software ID "${softwareId}" does not start with "${yearSuffix}" for tax year ${taxYear}. ` +
      `Updating to match tax year. Please ensure you're using the IRS-assigned Software ID for ${taxYear}.`
  );

  const remaining = softwareId.substring(2);
  return `${yearSuffix}${remaining}`.substring(0, 10);
}

// Validation function for state codes
//TODO: REMOVE AFTER DATA RESET
function validateStateCode(state: string | undefined): string {
  const validStates = [
    "AL",
    "AK",
    "AS",
    "AZ",
    "AR",
    "CA",
    "CO",
    "MP",
    "CT",
    "DE",
    "DC",
    "FM",
    "FL",
    "GA",
    "GU",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MH",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PW",
    "PA",
    "PR",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "VI",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
    "AA",
    "AE",
    "AP",
  ];

  if (!state) return "AA"; // Default to 'AA' if no state provided

  const upperState = state.trim().toUpperCase().substring(0, 2);

  // If the state is valid, return it
  if (validStates.includes(upperState)) {
    return upperState;
  }

  // If invalid, return the default 'AA'
  return "AA";
}

// Helper function to determine if a value should be treated as true
function isTrue(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value === true;
  }

  if (typeof value === "string") {
    const normalizedValue = value.toLowerCase().trim();
    return (
      normalizedValue === "true" ||
      normalizedValue === "1" ||
      normalizedValue === "yes"
    );
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return false;
}

// Function to clean last name to comply with IRS pattern requirements
function cleanLastName(name: string): string {
  if (!name) return "Unknown";

  // Remove spaces, digits, and special characters except hyphens
  const cleaned = name.replace(/[^A-Za-z\-]/g, "");

  // If nothing is left after cleaning, return "Unknown"
  return cleaned || "Unknown";
}

export async function generate1098TXml(
  ctx: EngineContext,
  records: Record1098T[],
  queryParams?: any
) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    return {
      validationResponse: [
        {
          error: true,
          message: "No records found or records input is invalid.",
        },
      ],
      xml: null,
      taxYear: null,
    };
  }

  // Filter records by fileType if specified in query parameters
  let filteredRecords = records;
  if (queryParams && queryParams.fileType) {
    const targetFileType = queryParams.fileType.toUpperCase();

    filteredRecords = records.filter((record) => {
      return (
        record["1098_YEAR"] &&
        (targetFileType === "1098-T" || targetFileType === "1098T")
      );
    });

    if (filteredRecords.length === 0) {
      return {
        validationResponse: [
          {
            error: true,
            message: `No records found with fileType: ${queryParams.fileType}`,
          },
        ],
        xml: null,
        taxYear: null,
      };
    }
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: false,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  // Get tax year from records or use default
  const taxYear = resolveIrsIntakeTaxYear(filteredRecords[0], ctx.irs.maxIntakeTaxYear);

  // Check if combined federal state program is enabled for this site
  // Get siteId from the first record (all records in a batch should have the same site)
  const firstRecord = filteredRecords[0];
  // EXTRACTION NOTE: the CFSF election used to be two database reads
  // (customerFiles -> site id, then siteYear.combinedFederal_stateProgram).
  // The engine has no database; the host answers through ctx.lookups.cfsfEnabled.
  const isCombinedFederalEnabled = await ctx.lookups.cfsfEnabled(
    firstRecord as unknown as Record<string, unknown>
  );

  // Generate unique record IDs and keep track for uniqueness validation
  const recordIds = new Set<string>();
  const recipientTINs = new Map<string, boolean>();

  // Legacy PG rows carry STUDENT_* columns; portal rows carry RECIPIENT_*.
  // Normalize so both paths feed the same field names (see student-record.ts).
  const normalizedRecords = filteredRecords.map(normalizeStudentRecord);
  const form1098TDetails = normalizedRecords.map((record) => {
    // Generate and validate unique record ID
    const recordId = record.id.toString();
    if (recordIds.has(recordId)) {
      throw new Error(`Duplicate record ID generated: ${recordId}`);
    }
    recordIds.add(recordId);

    // Track recipient TINs to ensure account numbers for duplicates
    const tin = record.RECIPIENT_ID_NUMBER || "";
    if (recipientTINs.has(tin) && !record.ACCOUNT_NUMBER) {
      console.warn(`Duplicate recipient TIN without account number: ${tin}`);
    }
    recipientTINs.set(tin, true);

    // Safely handle string operations
    const safeLastName = cleanLastName(record.RECIPIENT_NAME2 || "UNKNOWN");

    // Pub 1220 name control: the first four letters of the SURNAME. This took the first
    // name's, so DIEGO LOPEZ was filed as DIEG instead of LOPE, a mismatch against the IRS's
    // TIN records that comes back as a notice.
    const safePersonNameControl = personNameControl(record, record.RECIPIENT_NAME || "", safeLastName);
    const safeCity = record.RECIPIENT_CITY || "UNKNOWN";

    // Get valid state code for this record
    const validState = validateStateCode(record.RECIPIENT_STATE);

    // Create the form details with correct element ordering
    const formDetails: any = {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear,
    // NOTE: 1098-T is NOT part of the IRS Combined Federal/State Filing
    // program — neither CFSFElectionStateCd nor Form1098TTotalByStateGrp
    // exists in the 1098-T schema (TY2025 or TY2026). Emitting them made
    // every 1098-T transmission fail XSD validation with
    // "CFSFElectionStateCd: This element is not expected".
      RecordId: recordId,
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",
    };

    // Add PrevSubmittedRecRecipientGrp for corrections
    if (record.corrected) {
      formDetails.PrevSubmittedRecRecipientGrp = {
        PrevSubmittedTIN: record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        PrevSubmittedName: record.RECIPIENT_NAME,
      };
    }

    // RecipientDetail must come first according to schema
    formDetails.RecipientDetail = {
      TIN:
        transmitterData.testFileIndicator === "T"
          ? "000000000"
          : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
      TINSubmittedTypeCd: "INDIVIDUAL_TIN",
      ...(safePersonNameControl && {
        PersonNameControlTxt: safePersonNameControl,
      }),
      // PersonFirstNm must come before PersonLastNm; middle/suffix are the
      // TY2026-layout columns (schema-supported since TY2025, value-guarded)
      PersonName: buildStudentPersonName(
        record,
        record.RECIPIENT_NAME || "UNKNOWN",
        safeLastName
      ),
    };

    // Add address - handle foreign vs. US
    if (isTrue(record.FOREIGN_COUNTRY_INDICATOR) && record.FOREIGN_COUNTRY) {
      formDetails.RecipientDetail.MailingAddressGrp = {
        ForeignAddress: {
          AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
          ...(record.RECIPIENT_ADDRESS2 && {
            AddressLine2Txt: record.RECIPIENT_ADDRESS2,
          }),
          CityNm: safeCity,
          CountryCd: record.FOREIGN_COUNTRY.substring(0, 2),
          ...(record.RECIPIENT_ZIP && {
            ForeignPostalCd: record.RECIPIENT_ZIP,
          }),
        },
      };
    } else {
      formDetails.RecipientDetail.MailingAddressGrp = {
        USAddress: {
          // AddressLine1Txt is required
          AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
          ...(record.RECIPIENT_ADDRESS2 && {
            AddressLine2Txt: record.RECIPIENT_ADDRESS2,
          }),
          CityNm: safeCity,
          StateAbbreviationCd: validateStateCode(record.RECIPIENT_STATE),
          ZIPCd: record.RECIPIENT_ZIP || "00000",
        },
      };
    }

    // IMPORTANT: Add account number if available
    if (
      record.ACCOUNT_NUMBER ||
      record.BORROWER_ACCOUNT_NUMBER ||
      record.SCHOOL_STUDENT_ID
    ) {
      formDetails.RecipientAccountNum =
        record.ACCOUNT_NUMBER ||
        record.BORROWER_ACCOUNT_NUMBER ||
        record.SCHOOL_STUDENT_ID;
    }

    // CRITICAL: StudentTINSolicitationInd is required before StudentDetail
    formDetails.StudentTINSolicitationInd = "0";

    // Add form-specific fields in the EXACT order required by the IRS schema
    // Element order is *critical* for XML validation to pass

    // These first four are optional
    if (record.BOX1_PAYMENTS_RECEIVED) {
      formDetails.PaymentsReceivedQlfyExpnssAmt = parseAmount(
        record.BOX1_PAYMENTS_RECEIVED
      );
    }

    if (record.BOX4_ADJ_MADE_PRIOR_YR) {
      formDetails.PriorYearAdjustmentsAmt = parseAmount(
        record.BOX4_ADJ_MADE_PRIOR_YR
      );
    }

    if (record.BOX5_SCHOLARSHIP_GRANTS) {
      formDetails.GrantsOrScholarshipsAmt = parseAmount(
        record.BOX5_SCHOLARSHIP_GRANTS
      );
    }

    if (record.BOX6_ADJ_SCHOLARSHIP_PRIOR_YR) {
      formDetails.PYAdjGrantsOrScholarshipsAmt = parseAmount(
        record.BOX6_ADJ_SCHOLARSHIP_PRIOR_YR
      );
    }

    // These indicators are REQUIRED in this exact order
    formDetails.PymtRcvdIncldAmtSpcfdPeriodInd =
      record.BOX7_ACADEMIC_PERIOD_IND && isTrue(record.BOX7_ACADEMIC_PERIOD_IND)
        ? "1"
        : "0";
    formDetails.HalfTimeStudentInd =
      record.BOX8_HALF_TIME_STUDENT_IND &&
      isTrue(record.BOX8_HALF_TIME_STUDENT_IND)
        ? "1"
        : "0";
    formDetails.GraduateStudentInd =
      record.BOX9_GRADUATE_STUDENT_IND &&
      isTrue(record.BOX9_GRADUATE_STUDENT_IND)
        ? "1"
        : "0";

    // This final amount is optional
    if (record.BOX10_REIMBURSEMENT_OR_REFUND) {
      formDetails.InsReimbursementOrRefundAmt = parseAmount(
        record.BOX10_REIMBURSEMENT_OR_REFUND
      );
    }

    // Do NOT include StudentDetail element - validation errors show this is not allowed in the schema

    return formDetails;
  });

  // Calculate totals with corrected element names
  const calculateTotals = () => {
    let totals = {
      PaymentsReceivedQlfyExpnssAmt: 0,
      PriorYearAdjustmentsAmt: 0,
      GrantsOrScholarshipsAmt: 0,
      PYAdjGrantsOrScholarshipsAmt: 0,
      InsReimbursementOrRefundAmt: 0,
    };

    filteredRecords.forEach((record) => {
      if (record.BOX1_PAYMENTS_RECEIVED) {
        totals.PaymentsReceivedQlfyExpnssAmt += parseFloat(
          parseAmount(record.BOX1_PAYMENTS_RECEIVED)
        );
      }
      if (record.BOX4_ADJ_MADE_PRIOR_YR) {
        totals.PriorYearAdjustmentsAmt += parseFloat(
          parseAmount(record.BOX4_ADJ_MADE_PRIOR_YR)
        );
      }
      if (record.BOX5_SCHOLARSHIP_GRANTS) {
        totals.GrantsOrScholarshipsAmt += parseFloat(
          parseAmount(record.BOX5_SCHOLARSHIP_GRANTS)
        );
      }
      if (record.BOX6_ADJ_SCHOLARSHIP_PRIOR_YR) {
        totals.PYAdjGrantsOrScholarshipsAmt += parseFloat(
          parseAmount(record.BOX6_ADJ_SCHOLARSHIP_PRIOR_YR)
        );
      }
      if (record.BOX10_REIMBURSEMENT_OR_REFUND) {
        totals.InsReimbursementOrRefundAmt += parseFloat(
          parseAmount(record.BOX10_REIMBURSEMENT_OR_REFUND)
        );
      }
    });

    // Return totals in exact order per XSD schema (Form1098TTotalAmtGrpType)
    // TotalReportedAmt is NOT in the schema - removed
    return {
      PaymentsReceivedQlfyExpnssAmt:
        totals.PaymentsReceivedQlfyExpnssAmt.toFixed(2),
      PriorYearAdjustmentsAmt: totals.PriorYearAdjustmentsAmt.toFixed(
        2
      ),
      GrantsOrScholarshipsAmt: totals.GrantsOrScholarshipsAmt.toFixed(
        2
      ),
      PYAdjGrantsOrScholarshipsAmt: totals.PYAdjGrantsOrScholarshipsAmt.toFixed(
        2
      ),
      InsReimbursementOrRefundAmt: totals.InsReimbursementOrRefundAmt.toFixed(
        2
      ),
    };
  };

  const formTotals = calculateTotals();

  // Generate unique identifiers
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: form1098TDetails.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ContactPersonNm: transmitterData.contactName || "Contact Person",
              ContactPhoneNum: (
                transmitterData.contactTelephoneNumber || "0000000000"
              ).replace(/\D/g, ""),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum.replace(/\D/g, ""),
              }),
            },
            FormTypeCd: "1098T",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: isCombinedFederalEnabled ? "1" : "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: new Date().toISOString().split("T")[0], // Current date in YYYY-MM-DD format
              JuratPersonTitleTxt: "CFO",
              PersonNm: transmitterData.contactName || "Officer",
            },
            TotalReportedRcpntFormCnt: form1098TDetails.length.toString(),
            IRSubmission1FormTotals: {
              Form1098TTotalAmtGrp: formTotals,
            },
          },
          IRSubmission1Detail: {
            Form1098TDetail: form1098TDetails,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return {
      xml,
      taxYear,
      validationResponse: [], // Empty array means no validation errors
    };
  } catch (error) {
    console.error("Error generating XML:", error);
    return {
      validationResponse: [
        {
          error: true,
          message: "Failed to generate XML",
          details: error instanceof Error ? error.message : String(error),
        },
      ],
      xml: null,
      taxYear,
    };
  }
}
